import { Question, QuestionType } from '@/remoteApi/types/form-template.types';
import { Dispatch, FC, SetStateAction } from 'react';
import { AppSelect } from '../appSelect/AppSelect';
import { ModalPicker } from '../modalPicker/ModalPicker';
import { Box, Checkbox, NumberInput, Text, TextInput } from '@mantine/core';
import { AppDropzone } from '../appDropzone/AppDropzone';
import { AppTextarea } from '../appTextarea/AppTextarea';
import { UUID } from 'crypto';
import { FormAnswerMap, FormOptions, QuantityPerDimensionForm } from './ProductTypeForm';
import AppApi from '@/remoteApi/AppApi';

interface Props {
	question: Question;
	answersMap: Map<UUID, FormAnswerMap>;
	isLast: boolean;
	isValidating: boolean;
	hanldleOptionsChange: (
		key: keyof FormOptions,
		value: FormOptions[keyof FormOptions]
	) => void;
	setQuantityPerDimension: Dispatch<SetStateAction<QuantityPerDimensionForm>>;
}

export const QuestionRenderer: FC<Props> = ({
	question,
	answersMap,
	isLast,
	isValidating,
	hanldleOptionsChange,
	setQuantityPerDimension,
}) => {
	const formAnswer = answersMap.get(question.id) || {
		answerId: null,
		value: '',
		attachments: [],
		required: false,
	};

	const handleChange = (
		key: keyof FormAnswerMap,
		value: FormAnswerMap[keyof FormAnswerMap]
	) => {
		answersMap.set(question.id, { ...formAnswer, [key]: value });
	};

	const handleFilesChange = (files: File[]) => {
			Promise.all(files.map((file) => AppApi.createAttachment(file))).then(
				(attachments) => {
					answersMap.set(question.id, {
						...formAnswer,
						attachments: attachments,
					});
				}
			);
		},
		handleFileRemove = (attachmentId: UUID) => {
			const newFiles = formAnswer.attachments.filter(
				(file) => file.id !== attachmentId
			);
			answersMap.set(question.id, { ...formAnswer, attachments: newFiles });
		};

	let isErorr = isValidating && !formAnswer.answerId;

	switch (question.questionType) {
		case QuestionType.SELECT:
			return (
				<AppSelect
					mb={isLast ? undefined : 'md'}
					label={question.name}
					placeholder={`Wybierz ${question.name.toLocaleLowerCase()}`}
					data={question.answers.map((answer) => ({
						value: answer.id,
						label: `${answer.name}${question.suffix}`,
					}))}
					value={formAnswer.answerId}
					onChange={(value) => {
						if (value) {
							handleChange('answerId', value!);
						}
					}}
					error={isErorr}
				/>
			);
		case QuestionType.MODAL_PICKER:
		case QuestionType.MODEL_PICKER_TYPE:
			return (
				<ModalPicker
					question={question}
					answerId={formAnswer.answerId}
					handleChange={handleChange}
					isErorr={isErorr}
					hanldleOptionsChange={hanldleOptionsChange}
					setQuantityPerDimension={setQuantityPerDimension}
				/>
			);
		case QuestionType.TEXT:
			return (
				<TextInput
					mb={isLast ? undefined : 'md'}
					label={question.name}
					placeholder={question.placeholder || 'Wpisz odpowiedź'}
					value={formAnswer.value}
					onChange={({ target }) => handleChange('answerId', target.value)}
					error={isErorr}
				/>
			);
		case QuestionType.FILES:
			isErorr = isValidating && !formAnswer.attachments.length;

			return (
				<Box mb={isLast ? undefined : 'md'}>
					<Text mb="xs">Logotypy</Text>
					<AppDropzone
						formAnswer={formAnswer}
						handleChange={handleFilesChange}
						handleFileRemove={handleFileRemove}
						isErorr={isErorr}
					/>
				</Box>
			);
		case QuestionType.NUMBER:
			isErorr = isValidating && !formAnswer.value;

			return (
				<NumberInput
					mb={isLast ? undefined : 'md'}
					label={question.name}
					placeholder={`Wybierz ${question.name.toLocaleLowerCase()}`}
					step={0.1}
					suffix={question.suffix}
					value={formAnswer.value}
					onChange={(value) => handleChange('value', value.toString())}
					error={isErorr}
				/>
			);
		case QuestionType.TEXTAREA:
			isErorr = isValidating && !formAnswer.value;

			return (
				<AppTextarea
					mb={isLast ? undefined : 'md'}
					label={question.name}
					placeholder={question.placeholder || 'Wpisz odpowiedź'}
					tooltip={
						question.description ? { content: question.description } : undefined
					}
					value={formAnswer.value}
					onChange={({ target }) => handleChange('value', target.value)}
					error={isErorr}
				/>
			);
		case QuestionType.ADVANCED_OPTIONS:
			return (
				<Checkbox
					value="1"
					label="Pokaż opcje zaawansowane"
					mb={isLast ? undefined : 'md'}
				/>
			);
		default:
			break;
	}
};
